import { TextTutorialId, TextTutorial, TextTutorialPosition } from 'types';
import EventsManager from 'managers/events.manager';
import { centerPosition } from 'helpers/drag';
import { calculateDesiredTutorialPosition } from 'helpers/tutorial';
import { capitalize } from 'utils';

const POSITION_CENTER: TextTutorialPosition = {
  horizontal: 1,
  vertical: 1,
};

export const TUTORIALS: TextTutorial[] = [
  {
    id: TextTutorialId.FINAL,
    // tslint:disable-next-line:max-line-length
    text: 'You discovered your first <strong>final item</strong>!<br />Since final items don\'t mix with anything they will hide into the encyclopedia when you tap them.',
    watcher: (getters) => {
      const isMixedNew = getters.mixStatus === 'new';
      if (!isMixedNew) return false;

      const hasMixedFinal = getters.mixChildren.some(({ id }) => getters.elementById(id)!.final);
      return hasMixedFinal;
    },
    after: () => EventsManager.then('ANIMATION_NEW_END'),
    delay: 0.5 * 1000,
    getPosition: ($store) => {
      if (!$store.getters.workspaceFinal.length) {
        return POSITION_CENTER;
      }

      const position = centerPosition(
        $store.getters.workspaceFinal[0].position,
        $store.getters.workspaceBoxDimensions,
      );
      return calculateDesiredTutorialPosition(position);
    },
  },
  {
    id: TextTutorialId.BASE,
    // tslint:disable-next-line:max-line-length
    text: 'You discovered a new <strong>basic item</strong>!<br />Unlike the items you discover by mixing two things together, basic items can be unlocked by satisfying hidden goals!',
    watcher: (getters) => {
      const isMixedNew = getters.mixStatus === 'new';
      if (!isMixedNew) return false;

      return getters.mixHasBase;
    },
    after: () => EventsManager.then('ANIMATION_NEW_END'),
    delay: 0.5 * 1000,
    getPosition: ($store) => {
      const element = $store.getters.workspaceElements
        .find(({ elementId }) => {
          const item = $store.getters.elementById(elementId);
          return !!item && item.base;
        }) || null;
      if (!element) {
        return POSITION_CENTER;
      }

      const position = centerPosition(
        element.position,
        $store.getters.workspaceBoxDimensions,
      );
      return calculateDesiredTutorialPosition(position);
    },
  },
  {
    id: TextTutorialId.EXHAUSTED,
    // tslint:disable-next-line:max-line-length
    text: (getters) => {
      const elementId = getters.exhaustedVisible[getters.exhaustedVisible.length - 1];
      const element = getters.elementById(elementId);
      let beggining: string;
      if (!element) {
        beggining = 'You produced your first <strong>depleted item</strong>! ';
      } else {
        const name = capitalize(element.name);
        beggining = `${ name } is now a <strong>depleted item</strong>!<br />`;
      }

      // tslint:disable-next-line:max-line-length
      return `${ beggining } It means you discovered all uses of this item. Now when you tap it you'll send it straight to the encyclopedia!`;
    },
    watcher: (getters) => {
      const isMixedNew = getters.mixStatus === 'new';
      if (!isMixedNew) return false;
      if (!getters.mixFather || !getters.mixMother) return false;

      const mixParents = [getters.mixFather.elementId, getters.mixMother.elementId];
      const isParentExhausted = mixParents.some((id) => getters.elementIsExhausted(id) === true);
      return isParentExhausted;
    },
    after: (getters) => {
      if (getters.mixHasNewChildren) {
        return EventsManager.then('ANIMATION_NEW_END');
      }

      return Promise.resolve();
    },
    delay: 1 * 1000,
    getPosition: ($store) => {
      if (!$store.getters.workspaceExhausted.length) {
        return POSITION_CENTER;
      }

      const position = centerPosition(
        $store.getters.workspaceExhausted[0].position,
        $store.getters.workspaceBoxDimensions,
      );
      return calculateDesiredTutorialPosition(position);
    },
  },

  {
    id: TextTutorialId.GAME_END,
    // tslint:disable-next-line:max-line-length
    text: '<strong>Nice!</strong><br />You discovered all available items!<br />If you feel adventureous you can look for the remaining combinations!',
    watcher: (getters) => getters.hasAllElements,
    after: () => EventsManager.then('GAME_END_ANIMATION_END'),
    delay: 0.5 * 1000,
    getPosition: () => POSITION_CENTER,
  },
  {
    id: TextTutorialId.GAME_COMPLETION,
    // tslint:disable-next-line:max-line-length
    text: '<strong>Impressive!</strong><br />You discovered all available combinations!<br />Enjoy the art mode!',
    watcher: (getters) => getters.hasAllCleared,
    after: () => EventsManager.then('GAME_COMPLETION_ANIMATION_END'),
    delay: 0.5 * 1000,
    getPosition: () => POSITION_CENTER,
  },
];
